import { cx } from "@emotion/css";
import { Avatar, Box, Button, Chip, ListItem, ListItemAvatar, ListItemButton, ListItemText } from "@mui/material";
import { DRAWER_INFINITE_SCROLL_PAGE_SIZE, DRAWER_MAX_SELECTION_COUNT, KEYBOARD_ARROW_DOWN, SEARCH_DELAY_DEBOUNCE } from "app/appConstants";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AxiosResponse, GenericAbortSignal } from "axios";
import { MprcLink, MprcRequestErrorReport, MprcSpinnerIndicator } from "components";
import { IconButtonClearSearch } from "components/iconButtons/IconButtonClearSearch";
import { TextFieldFilter } from "components/MprcAdvFilter";
import { MprcAlertNoRecordDrawer } from "components/MprcAlertNoRecordDrawer";
import MprcAutoSizer from "components/MprcAutoSizer";
import { MprcBoxDrawer } from "components/MprcBoxDrawer";
import { SubordinateSelectorResponse, SubordinateSelectorResponsePaginatedList } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";

////////////////////////////////////////////////////////////////////////////////

type ItemData = {
    items: SubordinateSelectorResponse[];
    onItemClick: (item: SubordinateSelectorResponse) => void;
};

/**
 * Renderiza uma linha da Lista.
 * @param props
 */
function RenderRow(props: ListChildComponentProps<ItemData>) {
    const { index, style } = props;
    const { items, onItemClick } = props.data;

    if (index > items.length - 1) {
        return (
            <Box style={style} sx={{ mt: 1 }}>
                <MprcSpinnerIndicator />
            </Box>
        );
    }

    const item = items[index];

    return (
        <div key={index} style={style}>
            <ListItem disablePadding disableGutters>
                <ListItemButton disableGutters onClick={() => onItemClick(item)}>
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                        <Avatar
                            src={item.thumbnailUrl}
                            sx={(theme) => ({ width: 32, height: 32, backgroundColor: theme.palette.grey[200] })}
                        />
                    </ListItemAvatar >
                    <ListItemText
                        primary={item.name}
                        title={item.name}
                        primaryTypographyProps={{ noWrap: true, variant: "h6" }}
                    />

                </ListItemButton>
            </ListItem>
        </div>
    );
}

const MemoizedRenderRow = React.memo(RenderRow);

////////////////////////////////////////////////////////////////////////////////

export type SubordinateSelectorUsersFilter = {
    searchText: string;
    pageNumber: number;
    pageSize: number;
    managerId: string;
};

export type SubordinateSelectorDrawerResult = {
    userId: string;
    name: string;
    thumbnailId?: string | null;
    isDirectSubordinate: boolean;
};

interface IProps {
    managerId: string;
    title?: string;
    subtitle?: string;
    multi?: boolean;
    addSelectedButtonText?: string;
    onClose: (group: SubordinateSelectorDrawerResult[] | undefined) => void;
    onGetData?: (filter: SubordinateSelectorUsersFilter, signal: GenericAbortSignal | undefined) => Promise<AxiosResponse<SubordinateSelectorResponsePaginatedList>>;
}

/////////////////////////////////////////////////////////////////////////////////

export const SubordinatesSelectorDrawer = (props: IProps) => {

    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const { managerId, title, subtitle, multi = false, addSelectedButtonText, onGetData, onClose } = props;

    const [error, setError] = useState<any>(null);
    const [searchText, setSearchText] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [saveSelection, setSaveSelection] = useState<SubordinateSelectorResponse[] | undefined>(undefined);
    const [items, setItems] = useState<SubordinateSelectorResponse[]>([]);
    const [selected, setSelection] = useState<SubordinateSelectorResponse[]>([]);

    const currentPage = useRef<number>(1);
    const ignoreSelectionHistory = useRef<boolean>(false);
    const endOfRecords = useRef<boolean>(true);
    const infiniteLoaderRef = useRef<InfiniteLoader>(null);
    const hasMountedRef = useRef(false);

    let searchTextRef = useRef<HTMLInputElement>(null);
    const abortControllerRef = useRef<AbortController>(undefined);

    ////////////////////////////////////////////////////////////////////////////////

    ///
    async function loadData() {
        try {
            abortControllerRef.current = new AbortController();

            var result;
            if (onGetData) {
                result = await onGetData({
                    searchText,
                    pageNumber: currentPage.current,
                    pageSize: DRAWER_INFINITE_SCROLL_PAGE_SIZE,
                    managerId: managerId
                }, abortControllerRef.current.signal);
            }
            else {
                result = await api.v1.selectors.subordinates({
                    searchText: searchText,
                    pageNumber: currentPage.current,
                    pageSize: DRAWER_INFINITE_SCROLL_PAGE_SIZE,
                    managerId: managerId
                }, { signal: abortControllerRef.current.signal });
            }

            if (currentPage.current == 1) {
                // Reseta o cache.
                if (hasMountedRef.current) {
                    if (infiniteLoaderRef.current) {
                        infiniteLoaderRef.current.resetloadMoreItemsCache(true);
                    }
                }
                // Inicializa o array de itens.
                setItems(result.data.items);
            }
            else {
                // Adiciona mais itens no array.
                setItems([...items, ...result.data.items]);
            }

            // Há mais registros?
            endOfRecords.current = result.data.items.length < DRAWER_INFINITE_SCROLL_PAGE_SIZE;

            setTimeout(() => searchTextRef.current?.focus(), 100);
        }
        catch (e) {
            if (!isCanceledError(e)) {
                setError(e);
                endOfRecords.current = true;
            }
        }
        finally {
            setLoading(false);
            abortControllerRef.current = undefined;
        }
    }

    ///
    const loadNextPage = (startIndex: number, stopIndex: number) => {
        currentPage.current++;
        return new Promise<void>((resolve) => {
            loadData().then(() => resolve());
        });
    };

    ///
    const resetSearch = (ignoreHistory: boolean = false) => {
        currentPage.current = 1;
        ignoreSelectionHistory.current = ignoreHistory;
        setLoading(true);
        loadData();
        hasMountedRef.current = true;
    };

    // Se há mais itens para ser carregado, adicionamos UMA LINHA para que o Indicador de Carregamento apareça.
    const itemCount = !endOfRecords.current ? items.length + 1 : items.length;

    // Permite apenas uma página de items pode vez.
    const loadMoreItems = loading ? () => { } : loadNextPage;

    // Verifica se a linha está carregada, exceto o Indicador de Carregamento.
    const isItemLoaded = (index: number) => endOfRecords.current || index < items.length;

    // Ao selecionar um item da lista.
    const onItemClick = (item: SubordinateSelectorResponse) => {
        if (!multi)
            setSaveSelection([item]);
        else {
            if (selected.find(p => p.userId == item.userId) == null) {
                if (selected.length == DRAWER_MAX_SELECTION_COUNT)
                    enqueueSnackbar(t("User.UserSelectorDrawer.SelectionLimitedMessage", { count: DRAWER_MAX_SELECTION_COUNT }), { variant: "warning" });
                else
                    setSelection([...selected, item]);
            }
            else {
                enqueueSnackbar(t("User.UserSelectorDrawer.AlreadySelectedMessage", { name: item.name }), { variant: "error" });
            }
        }
    };

    ///
    const handleDirectSubordinatesSelection = () => {
        setSelection(items.filter(p => p.isDirectSubordinate));
    };

    ///
    const handleIndirectSubordinatesSelection = () => {
        setSelection(items);
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////////

    /// Executa a pesquisa depois de 600ms.
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            abortControllerRef.current?.abort();
            resetSearch();
        }, SEARCH_DELAY_DEBOUNCE);

        return () => {
            clearTimeout(delayDebounce);
            abortControllerRef.current?.abort();
        };
    }, [searchText]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <MprcBoxDrawer
            title={title || t("User.UserSelectorDrawer.Title")}
            subtitle={subtitle}
            onClose={
                (exitMode) => {
                    if (exitMode == "exit" || (exitMode == "escape" && selected.length == 0)) onClose(undefined);
                }
            }
            header={
                <>
                    <TextFieldFilter
                        autoFocus
                        fullWidth
                        autoComplete="off"
                        placeholder={t("Search.TextPlaceholder", { ns: "shared" })}
                        value={searchText}
                        onChange={() => {
                            setSearchText(searchTextRef.current?.value ?? "");
                        }}
                        onKeyDown={(e) => {
                            if (e.keyCode == 13) resetSearch();
                            else if (e.keyCode == KEYBOARD_ARROW_DOWN && searchTextRef.current?.value.length == 0) resetSearch(true);
                        }}
                        inputRef={searchTextRef}
                        slotProps={{
                            input: {
                                endAdornment:
                                    searchText != "" &&
                                    <IconButtonClearSearch
                                        size="small"
                                        onClick={() => {
                                            setSearchText("");
                                        }}
                                    />
                            }
                        }}
                    />
                </>
            }

            content={
                <>
                    {
                        error
                            ? <MprcRequestErrorReport error={error} />
                            : !loading && itemCount == 0
                                ? <MprcAlertNoRecordDrawer message={t(searchText.length > 0 ? "User.UserSelectorDrawer.NoRecordsFoundText" : "User.UserSelectorDrawer.FilterEmptyMessage")} />
                                : <MprcAutoSizer>
                                    {
                                        ({ width, height }) =>

                                            <InfiniteLoader
                                                isItemLoaded={isItemLoaded}
                                                itemCount={itemCount}
                                                loadMoreItems={loadMoreItems}
                                                ref={infiniteLoaderRef}
                                                threshold={10}
                                                minimumBatchSize={10}
                                            >
                                                {({ onItemsRendered, ref }) => (
                                                    <>
                                                        {
                                                            loading
                                                                ? <MprcSpinnerIndicator />
                                                                : <FixedSizeList
                                                                    height={height}
                                                                    width={width}
                                                                    itemData={{ items, onItemClick }}
                                                                    itemCount={itemCount}
                                                                    itemSize={40}
                                                                    onItemsRendered={onItemsRendered}
                                                                    ref={ref}
                                                                    className={cx(AppGlobalClassKeys.drawerFixedSizeList, AppGlobalClassKeys.thinScrollbar)}
                                                                >
                                                                    {MemoizedRenderRow}
                                                                </FixedSizeList>
                                                        }
                                                    </>
                                                )}
                                            </InfiniteLoader>
                                    }
                                </MprcAutoSizer>
                    }
                </>
            }
            footer={
                <div style={{ width: "100%" }}>
                    {
                        multi && selected.length == 0
                            ? (
                                <>
                                    <small>{t("User.UserSelectorDrawer.SelectedText")}:</small>
                                    &nbsp;
                                    <MprcLink variant="linkAction" onClick={handleDirectSubordinatesSelection}>Liderados Diretos</MprcLink>
                                    &nbsp;|&nbsp;
                                    <MprcLink variant="linkAction" onClick={handleIndirectSubordinatesSelection}>Todos</MprcLink>
                                </>
                            )
                            : multi && selected.length > 0 &&
                            <>
                                <div style={{ maxHeight: 200, overflow: "auto" }}>
                                    <p><small>{t("User.UserSelectorDrawer.SelectedText")}:</small></p>
                                    {
                                        selected.map((item, index) => (
                                            <Chip
                                                key={index}
                                                label={item.name}
                                                size="small"
                                                sx={{ mb: 0.5, mr: 0.5 }}
                                                avatar={<Avatar src={item.thumbnailUrl} />}
                                                onDelete={() =>
                                                    setSelection(selected.filter(p => p.userId != item.userId))
                                                }
                                            />
                                        ))
                                    }
                                </div>
                                <hr />
                                <div style={{ textAlign: "center" }}>
                                    <Button
                                        variant="contained"
                                        disabled={saveSelection != undefined}
                                        endIcon={saveSelection == undefined ? undefined : <MprcSpinnerIndicator />}
                                        onClick={() => onClose(selected)}
                                    >
                                        {addSelectedButtonText || t("User.UserSelectorDrawer.AddSelectedButtonText")}
                                    </Button >
                                </div>
                            </>
                    }
                </div>
            }
            requestIndicator={
                saveSelection != undefined ? "post" : undefined
            }
        />
    );
};
